import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bot, Send, Clock, CheckCircle, XCircle, Eye, Calendar, 
  Settings, BarChart3, Sparkles, RefreshCw, Play, Pause,
  Twitter, Linkedin, Facebook, Image, AlertCircle, Trash2,
  TrendingUp, Users, MousePointer, Target, Lightbulb, Plus,
  Edit, ChevronDown, ChevronUp, ExternalLink, Video, Film
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Platform Icons
const PlatformIcon = ({ platform, className = "w-5 h-5" }) => {
  const icons = {
    twitter: <Twitter className={className} />,
    linkedin: <Linkedin className={className} />,
    facebook: <Facebook className={className} />
  };
  return icons[platform] || null;
};

// Platform Colors - Improved for better visibility
const platformColors = {
  twitter: 'text-sky-300 bg-sky-500/20 border-sky-400/40',
  linkedin: 'text-blue-300 bg-blue-500/20 border-blue-400/40',
  facebook: 'text-indigo-300 bg-indigo-500/20 border-indigo-400/40'
};

// Status Badge Component - Improved colors
const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-amber-500/20 text-amber-300 border-amber-400/40',
    approved: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40',
    scheduled: 'bg-blue-500/20 text-blue-300 border-blue-400/40',
    published: 'bg-green-500/20 text-green-300 border-green-400/40',
    rejected: 'bg-red-500/20 text-red-300 border-red-400/40',
    failed: 'bg-red-500/20 text-red-300 border-red-400/40'
  };
  
  const labels = {
    pending: 'Ausstehend',
    approved: 'Genehmigt',
    scheduled: 'Geplant',
    published: 'Veröffentlicht',
    rejected: 'Abgelehnt',
    failed: 'Fehlgeschlagen'
  };
  
  return (
    <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
};

const AdminMarketingKI = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState({ running: false });
  
  // Generation Form - with video options
  const [generateForm, setGenerateForm] = useState({
    topic: '',
    platforms: ['twitter', 'linkedin', 'facebook'],
    post_type: 'promotional',
    include_image: false,
    include_video: true,
    video_duration: 4
  });
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Settings Form
  const [settingsForm, setSettingsForm] = useState({
    platforms: {
      twitter: { enabled: false, credentials: {} },
      linkedin: { enabled: false, credentials: {} },
      facebook: { enabled: false, credentials: {} }
    },
    posting_frequency: 'daily',
    auto_approve_threshold: 8,
    auto_mode: false,
    brand_guidelines: '',
    target_audience: ''
  });
  const [showCredentials, setShowCredentials] = useState({});
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [postsRes, settingsRes, analyticsRes, insightsRes, statusRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/marketing/posts`, { headers }),
        axios.get(`${BACKEND_URL}/api/marketing/settings`, { headers }),
        axios.get(`${BACKEND_URL}/api/marketing/analytics`, { headers }),
        axios.get(`${BACKEND_URL}/api/marketing/insights`, { headers }),
        axios.get(`${BACKEND_URL}/api/marketing/scheduler/status`, { headers })
      ]);
      
      setPosts(postsRes.data.posts || []);
      setSettings(settingsRes.data);
      setSettingsForm({
        platforms: settingsRes.data.platforms || settingsForm.platforms,
        posting_frequency: settingsRes.data.posting_frequency || 'daily',
        auto_approve_threshold: settingsRes.data.auto_approve_threshold || 8,
        auto_mode: settingsRes.data.auto_mode || false,
        brand_guidelines: settingsRes.data.brand_guidelines || '',
        target_audience: settingsRes.data.target_audience || ''
      });
      setAnalytics(analyticsRes.data);
      setInsights(insightsRes.data);
      setSchedulerStatus(statusRes.data);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Generate Post
  const handleGenerate = async () => {
    if (!generateForm.platforms.length) {
      toast.error('Bitte wählen Sie mindestens eine Plattform');
      return;
    }
    
    setIsGenerating(true);
    
    // Show info toast for video generation
    if (generateForm.include_video) {
      toast.info('Video wird generiert... Dies kann 2-5 Minuten dauern.', { duration: 10000 });
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/marketing/generate`,
        generateForm,
        { headers, timeout: 600000 } // 10 min timeout for video
      );
      
      toast.success('Post erfolgreich generiert!');
      setPosts([response.data, ...posts]);
      setGenerateForm({ ...generateForm, topic: '' });
      setActiveTab('posts');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler bei der Generierung';
      toast.error(msg);
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Approve Post
  const handleApprove = async (postId, scheduledAt = null) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/marketing/posts/${postId}/approve`,
        { scheduled_at: scheduledAt },
        { headers }
      );
      toast.success('Post genehmigt');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Genehmigen');
    }
  };
  
  // Reject Post
  const handleReject = async (postId, reason = '') => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/marketing/posts/${postId}/reject`,
        { reason },
        { headers }
      );
      toast.success('Post abgelehnt');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Ablehnen');
    }
  };
  
  // Publish Post Now
  const handlePublishNow = async (postId) => {
    if (!window.confirm('Post jetzt veröffentlichen?')) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/marketing/posts/${postId}/publish`,
        {},
        { headers }
      );
      toast.success('Post wird veröffentlicht...');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Veröffentlichen');
    }
  };
  
  // Delete Post
  const handleDelete = async (postId) => {
    if (!window.confirm('Post wirklich löschen?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/marketing/posts/${postId}`, { headers });
      toast.success('Post gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };
  
  // Save Settings
  const handleSaveSettings = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/marketing/settings`, settingsForm, { headers });
      toast.success('Einstellungen gespeichert');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };
  
  // Test Platform Connection
  const handleTestConnection = async (platform) => {
    const creds = settingsForm.platforms[platform]?.credentials;
    if (!creds || Object.keys(creds).length === 0) {
      toast.error('Bitte zuerst Zugangsdaten eingeben');
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/marketing/test-connection`,
        { platform, credentials: creds },
        { headers }
      );
      
      if (response.data.success) {
        toast.success(`${platform} Verbindung erfolgreich!`);
      } else {
        toast.error(`Verbindungsfehler: ${response.data.error}`);
      }
    } catch (error) {
      toast.error('Verbindungstest fehlgeschlagen');
    }
  };
  
  // Scheduler Control
  const handleSchedulerToggle = async () => {
    try {
      if (schedulerStatus.running) {
        await axios.post(`${BACKEND_URL}/api/marketing/scheduler/stop`, {}, { headers });
        toast.success('Scheduler gestoppt');
      } else {
        await axios.post(`${BACKEND_URL}/api/marketing/scheduler/start`, {}, { headers });
        toast.success('Scheduler gestartet');
      }
      loadData();
    } catch (error) {
      toast.error('Fehler beim Scheduler');
    }
  };
  
  // Render Dashboard Tab
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-cyan-500/20">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-cyan-500/20 border border-cyan-400/30">
              <Send className="w-6 h-6 text-cyan-300" />
            </div>
            <div>
              <p className="text-sm text-cyan-200/70 font-medium">Veröffentlicht</p>
              <p className="text-3xl font-bold text-white">{analytics?.total_posts || 0}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-purple-500/20">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-purple-500/20 border border-purple-400/30">
              <Eye className="w-6 h-6 text-purple-300" />
            </div>
            <div>
              <p className="text-sm text-purple-200/70 font-medium">Impressionen</p>
              <p className="text-3xl font-bold text-white">{analytics?.total_impressions?.toLocaleString() || 0}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-emerald-500/20">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-500/20 border border-emerald-400/30">
              <MousePointer className="w-6 h-6 text-emerald-300" />
            </div>
            <div>
              <p className="text-sm text-emerald-200/70 font-medium">Klicks</p>
              <p className="text-3xl font-bold text-white">{analytics?.total_clicks?.toLocaleString() || 0}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-amber-500/20">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-500/20 border border-amber-400/30">
              <Target className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <p className="text-sm text-amber-200/70 font-medium">Conversions</p>
              <p className="text-3xl font-bold text-white">{analytics?.total_conversions || 0}</p>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Scheduler Status */}
      <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl border ${schedulerStatus.running ? 'bg-green-500/20 border-green-400/30' : 'bg-slate-500/20 border-slate-400/30'}`}>
              {schedulerStatus.running ? (
                <Play className="w-6 h-6 text-green-300" />
              ) : (
                <Pause className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <div>
              <p className="font-semibold text-white text-lg">Auto-Posting Scheduler</p>
              <p className="text-sm text-slate-300">
                {schedulerStatus.running ? 'Läuft - Geplante Posts werden automatisch veröffentlicht' : 'Gestoppt - Manuelles Veröffentlichen erforderlich'}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSchedulerToggle}
            className={schedulerStatus.running 
              ? 'bg-red-500/20 border border-red-400/40 text-red-300 hover:bg-red-500/30' 
              : 'bg-green-500/20 border border-green-400/40 text-green-300 hover:bg-green-500/30'}
          >
            {schedulerStatus.running ? 'Stoppen' : 'Starten'}
          </Button>
        </div>
      </Card>
      
      {/* AI Insights */}
      {insights && (insights.insights?.length > 0 || insights.recommendations?.length > 0) && (
        <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-yellow-500/20">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-white">
            <Lightbulb className="w-5 h-5 text-yellow-400" />
            KI-Erkenntnisse
          </h3>
          
          {insights.insights?.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-yellow-200/70 mb-2 font-medium">Erkenntnisse:</p>
              <ul className="space-y-2">
                {insights.insights.map((insight, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-200">
                    <TrendingUp className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>{insight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {insights.recommendations?.length > 0 && (
            <div>
              <p className="text-sm text-yellow-200/70 mb-2 font-medium">Empfehlungen:</p>
              <ul className="space-y-2">
                {insights.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-200">
                    <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
      
      {/* Recent Posts */}
      <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Neueste Posts</h3>
          <Button variant="ghost" size="sm" onClick={() => setActiveTab('posts')} className="text-cyan-300 hover:text-cyan-200">
            Alle anzeigen →
          </Button>
        </div>
        
        {posts.slice(0, 3).length === 0 ? (
          <p className="text-slate-400 text-center py-8">Noch keine Posts vorhanden</p>
        ) : (
          <div className="space-y-3">
            {posts.slice(0, 3).map(post => (
              <div key={post.id} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <StatusBadge status={post.status} />
                      <div className="flex gap-1">
                        {post.platforms?.map(p => (
                          <span key={p} className={`p-1 rounded ${platformColors[p]}`}>
                            <PlatformIcon platform={p} className="w-3 h-3" />
                          </span>
                        ))}
                      </div>
                      {post.video_url && (
                        <span className="px-2 py-1 rounded-full text-xs bg-pink-500/20 text-pink-300 border border-pink-400/30 flex items-center gap-1">
                          <Film className="w-3 h-3" /> Video
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-200 line-clamp-2">
                      {post.content?.twitter?.text || post.content?.linkedin?.text || 'Kein Inhalt'}
                    </p>
                  </div>
                  {post.image_url && (
                    <img src={post.image_url} alt="" className="w-16 h-16 rounded-lg object-cover ml-3" />
                  )}
                  {post.video_url && !post.image_url && (
                    <div className="w-16 h-16 rounded-lg bg-pink-500/20 border border-pink-400/30 flex items-center justify-center ml-3">
                      <Film className="w-8 h-8 text-pink-300" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
  
  // Render Generate Tab
  const renderGenerate = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-cyan-500/20">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2 text-white">
          <Sparkles className="w-5 h-5 text-cyan-400" />
          Social Media Post generieren
        </h2>
        
        <div className="space-y-5">
          {/* Topic Input */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-200">Thema (optional)</label>
            <input
              type="text"
              value={generateForm.topic}
              onChange={(e) => setGenerateForm({ ...generateForm, topic: e.target.value })}
              placeholder="z.B. Neue KI-Diagnose Feature, Wintercheck Angebot..."
              className="w-full px-4 py-3 bg-slate-800/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-cyan-500/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 transition"
            />
            <p className="text-xs text-slate-400 mt-1">Leer lassen für automatische Themenauswahl</p>
          </div>
          
          {/* Platforms */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-200">Plattformen</label>
            <div className="flex gap-3 flex-wrap">
              {['twitter', 'linkedin', 'facebook'].map(platform => (
                <button
                  key={platform}
                  onClick={() => {
                    const platforms = generateForm.platforms.includes(platform)
                      ? generateForm.platforms.filter(p => p !== platform)
                      : [...generateForm.platforms, platform];
                    setGenerateForm({ ...generateForm, platforms });
                  }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition font-medium ${
                    generateForm.platforms.includes(platform)
                      ? platformColors[platform]
                      : 'bg-slate-800/40 border-slate-600/40 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  <PlatformIcon platform={platform} className="w-4 h-4" />
                  <span className="capitalize">{platform}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Post Type */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-200">Post-Typ</label>
            <select
              value={generateForm.post_type}
              onChange={(e) => setGenerateForm({ ...generateForm, post_type: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800/60 border border-slate-600/50 rounded-xl text-white focus:border-cyan-500/50 focus:outline-none"
            >
              <option value="promotional">🎯 Werbung / Promotion</option>
              <option value="educational">📚 Wissen / Tipps</option>
              <option value="engagement">💬 Engagement / Community</option>
              <option value="news">📰 News / Updates</option>
            </select>
          </div>
          
          {/* Media Options */}
          <div className="p-4 bg-slate-800/40 rounded-xl border border-slate-600/30">
            <label className="block text-sm font-semibold mb-3 text-slate-200">Media-Optionen</label>
            
            {/* Video Option */}
            <div className="flex items-center gap-3 mb-3">
              <input
                type="checkbox"
                id="include_video"
                checked={generateForm.include_video}
                onChange={(e) => setGenerateForm({ 
                  ...generateForm, 
                  include_video: e.target.checked,
                  include_image: e.target.checked ? false : generateForm.include_image 
                })}
                className="w-5 h-5 rounded bg-slate-700 border-slate-500 text-pink-500 focus:ring-pink-500/30"
              />
              <label htmlFor="include_video" className="flex items-center gap-2 text-slate-200 cursor-pointer">
                <Film className="w-5 h-5 text-pink-400" />
                <span className="font-medium">Video mit Sora 2 generieren</span>
                <span className="text-xs text-pink-300 bg-pink-500/20 px-2 py-0.5 rounded">NEU!</span>
              </label>
            </div>
            
            {/* Video Duration */}
            {generateForm.include_video && (
              <div className="ml-8 mb-3">
                <label className="block text-xs text-slate-400 mb-1">Video-Länge</label>
                <select
                  value={generateForm.video_duration}
                  onChange={(e) => setGenerateForm({ ...generateForm, video_duration: parseInt(e.target.value) })}
                  className="px-3 py-2 bg-slate-700/60 border border-slate-600/50 rounded-lg text-white text-sm"
                >
                  <option value="4">4 Sekunden (schnell)</option>
                  <option value="8">8 Sekunden</option>
                  <option value="12">12 Sekunden (lang)</option>
                </select>
              </div>
            )}
            
            {/* Image Option */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="include_image"
                checked={generateForm.include_image}
                disabled={generateForm.include_video}
                onChange={(e) => setGenerateForm({ ...generateForm, include_image: e.target.checked })}
                className="w-5 h-5 rounded bg-slate-700 border-slate-500 text-purple-500 focus:ring-purple-500/30 disabled:opacity-50"
              />
              <label htmlFor="include_image" className={`flex items-center gap-2 cursor-pointer ${generateForm.include_video ? 'text-slate-500' : 'text-slate-200'}`}>
                <Image className="w-5 h-5 text-purple-400" />
                <span className="font-medium">Bild mit GPT Image generieren</span>
              </label>
            </div>
            
            {generateForm.include_video && (
              <p className="text-xs text-slate-400 mt-2 ml-8">
                ⚠️ Video-Generierung dauert 2-5 Minuten
              </p>
            )}
          </div>
          
          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || generateForm.platforms.length === 0}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 py-4 text-white font-semibold text-lg disabled:opacity-50"
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-2">
                <RefreshCw className="w-5 h-5 animate-spin" />
                {generateForm.include_video ? 'Generiere Video...' : 'Generiere...'}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Bot className="w-5 h-5" />
                Post generieren
              </span>
            )}
          </Button>
        </div>
      </Card>
      
      {/* Platform Setup Info */}
      <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-white">
          <Settings className="w-5 h-5 text-slate-400" />
          Platform Setup
        </h2>
        
        <div className="space-y-4">
          {/* Twitter Setup */}
          <div className="p-4 bg-slate-800/40 rounded-xl border border-slate-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Twitter className="w-5 h-5 text-sky-400" />
              <span className="font-semibold text-white">Twitter / X</span>
              {settings?.platforms?.twitter?.enabled && (
                <CheckCircle className="w-4 h-4 text-green-400 ml-auto" />
              )}
            </div>
            <p className="text-sm text-slate-300">
              Benötigt: API Key, API Secret, Access Token, Access Token Secret
            </p>
            <a 
              href="https://developer.twitter.com/en/portal/dashboard" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1 mt-1"
            >
              Twitter Developer Portal <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          
          {/* LinkedIn Setup */}
          <div className="p-4 bg-slate-800/40 rounded-xl border border-slate-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Linkedin className="w-5 h-5 text-blue-400" />
              <span className="font-semibold text-white">LinkedIn</span>
              {settings?.platforms?.linkedin?.enabled && (
                <CheckCircle className="w-4 h-4 text-green-400 ml-auto" />
              )}
            </div>
            <p className="text-sm text-slate-300">
              Benötigt: OAuth 2.0 Access Token, Organization ID (für Company Pages)
            </p>
            <a 
              href="https://www.linkedin.com/developers/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 mt-1"
            >
              LinkedIn Developer Portal <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          
          {/* Facebook Setup */}
          <div className="p-4 bg-slate-800/40 rounded-xl border border-slate-600/30">
            <div className="flex items-center gap-2 mb-2">
              <Facebook className="w-5 h-5 text-indigo-400" />
              <span className="font-semibold text-white">Facebook</span>
              {settings?.platforms?.facebook?.enabled && (
                <CheckCircle className="w-4 h-4 text-green-400 ml-auto" />
              )}
            </div>
            <p className="text-sm text-slate-300">
              Benötigt: Page Access Token, Page ID
            </p>
            <a 
              href="https://developers.facebook.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 mt-1"
            >
              Facebook Developer Portal <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          
          <Button 
            variant="outline" 
            className="w-full mt-4 border-slate-600 text-slate-200 hover:bg-slate-800"
            onClick={() => setActiveTab('settings')}
          >
            <Settings className="w-4 h-4 mr-2" />
            Plattformen konfigurieren
          </Button>
        </div>
      </Card>
    </div>
  );
  
  // Render Posts Tab
  const renderPosts = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white">Alle Posts ({posts.length})</h2>
        <Button onClick={loadData} variant="outline" size="sm" className="border-slate-600 text-slate-200">
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>
      
      {posts.length === 0 ? (
        <Card className="p-12 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10 text-center">
          <Bot className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 mb-4">Noch keine Posts generiert</p>
          <Button onClick={() => setActiveTab('generate')} className="bg-cyan-500 hover:bg-cyan-600">
            <Plus className="w-4 h-4 mr-2" />
            Ersten Post erstellen
          </Button>
        </Card>
      ) : (
        posts.map(post => (
          <PostCard 
            key={post.id} 
            post={post}
            onApprove={handleApprove}
            onReject={handleReject}
            onPublish={handlePublishNow}
            onDelete={handleDelete}
          />
        ))
      )}
    </div>
  );
  
  // Render Settings Tab
  const renderSettings = () => (
    <div className="space-y-6">
      {/* Platform Credentials */}
      <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
        <h2 className="text-xl font-bold mb-6 text-white">Plattform-Zugangsdaten</h2>
        
        {/* Twitter */}
        <div className="mb-6">
          <button
            onClick={() => setShowCredentials({ ...showCredentials, twitter: !showCredentials.twitter })}
            className="flex items-center justify-between w-full p-4 bg-slate-800/40 rounded-xl border border-slate-600/30 hover:bg-slate-800/60 transition"
          >
            <div className="flex items-center gap-3">
              <Twitter className="w-5 h-5 text-sky-400" />
              <span className="font-semibold text-white">Twitter / X</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                settingsForm.platforms.twitter?.enabled 
                  ? 'bg-green-500/20 text-green-300 border border-green-400/30' 
                  : 'bg-slate-700/50 text-slate-400'
              }`}>
                {settingsForm.platforms.twitter?.enabled ? 'Verbunden' : 'Nicht verbunden'}
              </span>
              {showCredentials.twitter ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </div>
          </button>
          
          {showCredentials.twitter && (
            <div className="mt-3 p-4 bg-slate-800/30 rounded-xl space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={settingsForm.platforms.twitter?.enabled || false}
                  onChange={(e) => setSettingsForm({
                    ...settingsForm,
                    platforms: {
                      ...settingsForm.platforms,
                      twitter: { ...settingsForm.platforms.twitter, enabled: e.target.checked }
                    }
                  })}
                  className="w-4 h-4 rounded"
                />
                <label className="text-slate-200">Twitter aktivieren</label>
              </div>
              
              {['api_key', 'api_secret', 'access_token', 'access_token_secret'].map(field => (
                <input
                  key={field}
                  type="password"
                  placeholder={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  value={settingsForm.platforms.twitter?.credentials?.[field] || ''}
                  onChange={(e) => setSettingsForm({
                    ...settingsForm,
                    platforms: {
                      ...settingsForm.platforms,
                      twitter: {
                        ...settingsForm.platforms.twitter,
                        credentials: {
                          ...settingsForm.platforms.twitter?.credentials,
                          [field]: e.target.value
                        }
                      }
                    }
                  })}
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-white placeholder-slate-400"
                />
              ))}
              
              <Button size="sm" variant="outline" onClick={() => handleTestConnection('twitter')} className="border-slate-600 text-slate-200">
                Verbindung testen
              </Button>
            </div>
          )}
        </div>
        
        {/* LinkedIn */}
        <div className="mb-6">
          <button
            onClick={() => setShowCredentials({ ...showCredentials, linkedin: !showCredentials.linkedin })}
            className="flex items-center justify-between w-full p-4 bg-slate-800/40 rounded-xl border border-slate-600/30 hover:bg-slate-800/60 transition"
          >
            <div className="flex items-center gap-3">
              <Linkedin className="w-5 h-5 text-blue-400" />
              <span className="font-semibold text-white">LinkedIn</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                settingsForm.platforms.linkedin?.enabled 
                  ? 'bg-green-500/20 text-green-300 border border-green-400/30' 
                  : 'bg-slate-700/50 text-slate-400'
              }`}>
                {settingsForm.platforms.linkedin?.enabled ? 'Verbunden' : 'Nicht verbunden'}
              </span>
              {showCredentials.linkedin ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </div>
          </button>
          
          {showCredentials.linkedin && (
            <div className="mt-3 p-4 bg-slate-800/30 rounded-xl space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={settingsForm.platforms.linkedin?.enabled || false}
                  onChange={(e) => setSettingsForm({
                    ...settingsForm,
                    platforms: {
                      ...settingsForm.platforms,
                      linkedin: { ...settingsForm.platforms.linkedin, enabled: e.target.checked }
                    }
                  })}
                  className="w-4 h-4 rounded"
                />
                <label className="text-slate-200">LinkedIn aktivieren</label>
              </div>
              
              {['access_token', 'organization_id'].map(field => (
                <input
                  key={field}
                  type={field.includes('token') ? 'password' : 'text'}
                  placeholder={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  value={settingsForm.platforms.linkedin?.credentials?.[field] || ''}
                  onChange={(e) => setSettingsForm({
                    ...settingsForm,
                    platforms: {
                      ...settingsForm.platforms,
                      linkedin: {
                        ...settingsForm.platforms.linkedin,
                        credentials: {
                          ...settingsForm.platforms.linkedin?.credentials,
                          [field]: e.target.value
                        }
                      }
                    }
                  })}
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-white placeholder-slate-400"
                />
              ))}
              
              <Button size="sm" variant="outline" onClick={() => handleTestConnection('linkedin')} className="border-slate-600 text-slate-200">
                Verbindung testen
              </Button>
            </div>
          )}
        </div>
        
        {/* Facebook */}
        <div className="mb-6">
          <button
            onClick={() => setShowCredentials({ ...showCredentials, facebook: !showCredentials.facebook })}
            className="flex items-center justify-between w-full p-4 bg-slate-800/40 rounded-xl border border-slate-600/30 hover:bg-slate-800/60 transition"
          >
            <div className="flex items-center gap-3">
              <Facebook className="w-5 h-5 text-indigo-400" />
              <span className="font-semibold text-white">Facebook</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                settingsForm.platforms.facebook?.enabled 
                  ? 'bg-green-500/20 text-green-300 border border-green-400/30' 
                  : 'bg-slate-700/50 text-slate-400'
              }`}>
                {settingsForm.platforms.facebook?.enabled ? 'Verbunden' : 'Nicht verbunden'}
              </span>
              {showCredentials.facebook ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </div>
          </button>
          
          {showCredentials.facebook && (
            <div className="mt-3 p-4 bg-slate-800/30 rounded-xl space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={settingsForm.platforms.facebook?.enabled || false}
                  onChange={(e) => setSettingsForm({
                    ...settingsForm,
                    platforms: {
                      ...settingsForm.platforms,
                      facebook: { ...settingsForm.platforms.facebook, enabled: e.target.checked }
                    }
                  })}
                  className="w-4 h-4 rounded"
                />
                <label className="text-slate-200">Facebook aktivieren</label>
              </div>
              
              {['page_access_token', 'page_id'].map(field => (
                <input
                  key={field}
                  type={field.includes('token') ? 'password' : 'text'}
                  placeholder={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  value={settingsForm.platforms.facebook?.credentials?.[field] || ''}
                  onChange={(e) => setSettingsForm({
                    ...settingsForm,
                    platforms: {
                      ...settingsForm.platforms,
                      facebook: {
                        ...settingsForm.platforms.facebook,
                        credentials: {
                          ...settingsForm.platforms.facebook?.credentials,
                          [field]: e.target.value
                        }
                      }
                    }
                  })}
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-sm text-white placeholder-slate-400"
                />
              ))}
              
              <Button size="sm" variant="outline" onClick={() => handleTestConnection('facebook')} className="border-slate-600 text-slate-200">
                Verbindung testen
              </Button>
            </div>
          )}
        </div>
      </Card>
      
      {/* General Settings */}
      <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
        <h2 className="text-xl font-bold mb-6 text-white">Allgemeine Einstellungen</h2>
        
        <div className="space-y-5">
          {/* Posting Frequency */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-200">Posting-Frequenz</label>
            <select
              value={settingsForm.posting_frequency}
              onChange={(e) => setSettingsForm({ ...settingsForm, posting_frequency: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800/60 border border-slate-600/50 rounded-xl text-white"
            >
              <option value="3x_weekly">3x pro Woche</option>
              <option value="daily">Täglich</option>
              <option value="2x_daily">2x täglich</option>
              <option value="weekly">Wöchentlich</option>
            </select>
          </div>
          
          {/* Auto Approve Threshold */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-200">
              Auto-Approve Schwellenwert: <span className="text-cyan-400">{settingsForm.auto_approve_threshold}</span>
            </label>
            <input
              type="range"
              min="1"
              max="10"
              value={settingsForm.auto_approve_threshold}
              onChange={(e) => setSettingsForm({ ...settingsForm, auto_approve_threshold: parseInt(e.target.value) })}
              className="w-full accent-cyan-500"
            />
            <p className="text-xs text-slate-400 mt-1">
              Posts mit KI-Score ≥ {settingsForm.auto_approve_threshold} werden automatisch genehmigt
            </p>
          </div>
          
          {/* Auto Mode */}
          <div className="flex items-center gap-3 p-4 bg-slate-800/40 rounded-xl border border-slate-600/30">
            <input
              type="checkbox"
              id="auto_mode"
              checked={settingsForm.auto_mode}
              onChange={(e) => setSettingsForm({ ...settingsForm, auto_mode: e.target.checked })}
              className="w-5 h-5 rounded"
            />
            <label htmlFor="auto_mode" className="flex flex-col cursor-pointer">
              <span className="font-semibold text-white">Vollautomatischer Modus</span>
              <span className="text-sm text-slate-400">
                Posts werden ohne manuelle Freigabe veröffentlicht
              </span>
            </label>
          </div>
          
          {/* Brand Guidelines */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-200">Marken-Richtlinien</label>
            <textarea
              value={settingsForm.brand_guidelines}
              onChange={(e) => setSettingsForm({ ...settingsForm, brand_guidelines: e.target.value })}
              placeholder="z.B. Professioneller Ton, technische Expertise betonen, CarCortex-AI Vorteile hervorheben..."
              rows={3}
              className="w-full px-4 py-3 bg-slate-800/60 border border-slate-600/50 rounded-xl resize-none text-white placeholder-slate-400"
            />
          </div>
          
          {/* Target Audience */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-200">Zielgruppe</label>
            <input
              type="text"
              value={settingsForm.target_audience}
              onChange={(e) => setSettingsForm({ ...settingsForm, target_audience: e.target.value })}
              placeholder="z.B. Werkstätten, Flottenmanager, Autobesitzer"
              className="w-full px-4 py-3 bg-slate-800/60 border border-slate-600/50 rounded-xl text-white placeholder-slate-400"
            />
          </div>
          
          <Button onClick={handleSaveSettings} className="w-full bg-cyan-500 hover:bg-cyan-600 py-3 text-white font-semibold">
            Einstellungen speichern
          </Button>
        </div>
      </Card>
    </div>
  );
  
  return (
    <div className="p-6" style={{ backgroundColor: '#0D1117', minHeight: '100vh', color: '#E6EDF3' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3 text-white">
            <div className="p-2 rounded-xl bg-gradient-to-br from-cyan-500/30 to-blue-500/30 border border-cyan-400/30">
              <Bot className="w-8 h-8 text-cyan-400" />
            </div>
            Marketing KI
          </h1>
          <p className="text-slate-400">Automatisierte Social Media Posts mit selbstlernender KI</p>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-slate-700/50 overflow-x-auto">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'generate', label: 'Generieren', icon: Sparkles },
            { id: 'posts', label: 'Posts', icon: Send },
            { id: 'settings', label: 'Einstellungen', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 flex items-center gap-2 border-b-2 transition-all whitespace-nowrap font-medium ${
                activeTab === tab.id
                  ? 'border-cyan-400 text-cyan-400 bg-cyan-500/10'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/30'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-cyan-400" />
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'generate' && renderGenerate()}
            {activeTab === 'posts' && renderPosts()}
            {activeTab === 'settings' && renderSettings()}
          </>
        )}
      </div>
    </div>
  );
};

// Post Card Component - Improved colors
const PostCard = ({ post, onApprove, onReject, onPublish, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card className="p-6 bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10 relative">
      <div className="flex items-start gap-4">
        {/* Media Preview */}
        {(post.image_url || post.video_url) && (
          <div className="flex-shrink-0">
            {post.video_url ? (
              <div className="relative w-28 h-28 rounded-xl overflow-hidden bg-slate-800 border border-pink-500/30">
                <video 
                  src={`${BACKEND_URL}${post.video_url}`} 
                  className="w-full h-full object-cover"
                  muted
                  loop
                  onMouseEnter={(e) => e.target.play()}
                  onMouseLeave={(e) => e.target.pause()}
                />
                <div className="absolute bottom-1 right-1 bg-pink-500/80 px-1.5 py-0.5 rounded text-xs text-white font-medium flex items-center gap-1">
                  <Film className="w-3 h-3" /> Video
                </div>
              </div>
            ) : post.image_url && (
              <img 
                src={post.image_url} 
                alt="" 
                className="w-28 h-28 rounded-xl object-cover border border-purple-500/30"
              />
            )}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <StatusBadge status={post.status} />
            
            {post.ai_score && (
              <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-violet-500/20 text-violet-300 border border-violet-400/30">
                KI Score: {post.ai_score}
              </span>
            )}
            
            <div className="flex gap-1.5">
              {post.platforms?.map(p => (
                <span key={p} className={`p-1.5 rounded-lg ${platformColors[p]}`}>
                  <PlatformIcon platform={p} className="w-4 h-4" />
                </span>
              ))}
            </div>
            
            {post.video_url && (
              <span className="px-2 py-1 rounded-full text-xs bg-pink-500/20 text-pink-300 border border-pink-400/30 flex items-center gap-1">
                <Film className="w-3 h-3" /> Video
              </span>
            )}
            
            {post.video_error && (
              <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-300 border border-red-400/30 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Video fehlgeschlagen
              </span>
            )}
          </div>
          
          {/* Content Preview */}
          <div className="space-y-2">
            {post.content?.twitter && (
              <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <div className="flex items-center gap-2 text-xs text-sky-300 mb-1">
                  <Twitter className="w-3 h-3" />
                  Twitter
                </div>
                <p className="text-sm text-slate-200">{post.content.twitter.text}</p>
                {post.content.twitter.hashtags?.length > 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    {post.content.twitter.hashtags.map(t => `#${t}`).join(' ')}
                  </p>
                )}
              </div>
            )}
            
            {expanded && (
              <>
                {post.content?.linkedin && (
                  <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
                    <div className="flex items-center gap-2 text-xs text-blue-300 mb-1">
                      <Linkedin className="w-3 h-3" />
                      LinkedIn
                    </div>
                    <p className="text-sm whitespace-pre-wrap text-slate-200">{post.content.linkedin.text}</p>
                  </div>
                )}
                
                {post.content?.facebook && (
                  <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
                    <div className="flex items-center gap-2 text-xs text-indigo-300 mb-1">
                      <Facebook className="w-3 h-3" />
                      Facebook
                    </div>
                    <p className="text-sm whitespace-pre-wrap text-slate-200">{post.content.facebook.text}</p>
                  </div>
                )}
              </>
            )}
            
            {(post.content?.linkedin || post.content?.facebook) && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
              >
                {expanded ? 'Weniger anzeigen' : 'Alle Plattformen anzeigen'}
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            )}
          </div>
          
          {/* Video Error Message */}
          {post.video_error && (
            <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-300 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{post.video_error}</span>
              </p>
            </div>
          )}
          
          {/* Meta */}
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
            <span>Erstellt: {new Date(post.created_at).toLocaleString('de-DE')}</span>
            {post.scheduled_at && (
              <span className="flex items-center gap-1 text-blue-300">
                <Calendar className="w-3 h-3" />
                Geplant: {new Date(post.scheduled_at).toLocaleString('de-DE')}
              </span>
            )}
          </div>
          
          {/* Actions */}
          {post.status === 'pending' && (
            <div className="flex gap-2 mt-4 flex-wrap">
              <Button size="sm" onClick={() => onApprove(post.id)} className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/30">
                <CheckCircle className="w-4 h-4 mr-1" />
                Genehmigen
              </Button>
              <Button size="sm" variant="outline" onClick={() => onReject(post.id)} className="border-red-400/40 text-red-300 hover:bg-red-500/20">
                <XCircle className="w-4 h-4 mr-1" />
                Ablehnen
              </Button>
              <Button size="sm" variant="outline" onClick={() => onPublish(post.id)} className="border-slate-600 text-slate-300 hover:bg-slate-800">
                <Send className="w-4 h-4 mr-1" />
                Jetzt posten
              </Button>
            </div>
          )}
          
          {(post.status === 'approved' || post.status === 'scheduled') && (
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={() => onPublish(post.id)} className="bg-blue-500/20 border border-blue-400/40 text-blue-300 hover:bg-blue-500/30">
                <Send className="w-4 h-4 mr-1" />
                Jetzt veröffentlichen
              </Button>
            </div>
          )}
        </div>
        
        {/* Delete Button */}
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={() => onDelete(post.id)}
          className="absolute top-4 right-4 text-slate-500 hover:text-red-400 hover:bg-red-500/10"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
};

export default AdminMarketingKI;
